/**
 * Step1 / Step2 のパネル — 放送テキスト入力と、辞書適用済 SSML の確認表示。
 *
 * SBI-001 (Step1 簡素化) で、AI 改善中心の構成からプレーンテキスト入力中心の構成へ変更した。
 * SBI-002 (Step1 プレーンテキスト Audio 対応) で、放送テキストをそのまま音声にして確認できるようにした。
 * SBI-003 (辞書適用Audio作成) で、Step1 の放送テキストから Step2 の辞書適用済 SSML を生成できるようにした。
 * SBI-005 (Step2 Audio 操作対応) で、Step2 の SSML 編集欄の直下にも音声操作を追加した。
 *
 *   - Step1: 放送テキスト入力 + 音声操作 (Audio プレイヤー / 変更内容でAudio再作成 / ダウンロード)
 *            + 「辞書適用Audio作成」ボタン (読み上げ要望 / AI 解析 / 改善理由は廃止)
 *   - Step2: 辞書適用済 SSML の確認・編集 (SBI-004 / 編集可能 / 改行保持 / スクロール可能)
 *            + 音声操作 (SBI-005 / 編集した SSML で Audio を再作成して試聴・ダウンロード)
 *
 * ## 設計上の約束
 *
 * - 放送テキストの正源は **親 (ページ)** が持つ (制御コンポーネント)。
 *   Step3 の入力・ダウンロード名の元になるため、本コンポーネントの内部 state にしない。
 * - AI を呼ばない。入力のたびに課金される経路を持たない。
 * - 辞書適用は **利用者が「辞書適用Audio作成」を押したときだけ** 行う (自動実行しない)。
 *   サーバ (`POST /audio-preview`) は辞書適用後に Polly 合成まで行うため、入力のたびに呼ぶと課金が続く。
 * - 辞書適用の結果 (Step2 の SSML) は本コンポーネントが持つ。ページの「リセット」は
 *   パネルの `key` を変えて作り直すため、結果も一緒に消える。
 * - Step2 の SSML は利用者が直接編集できる (SBI-004)。編集内容は本コンポーネントの state に保持し、
 *   「辞書適用Audio作成」を押し直すと **辞書適用の結果で上書き** される (最新の放送テキストを反映するため)。
 * - Step2 の音声 (SBI-005) は **編集中の SSML をそのまま** Polly へ渡す。辞書は再適用しない
 *   (`previewAudio` は `applyDictionary: false`。適用済みの SSML に二重で辞書をかけない)。
 *   自動では合成せず、「変更内容でAudio再作成」を押したときだけ合成する。
 *   Step3 への連携は後続 SBI 以降で扱う。
 * - Step3 の「Audio サンプル作成」ボタンの差し込み口 (`step2ActionsRef`) は維持する。
 *
 * ⚠️ コンポーネント名 `Step1AnalyzePanel` は AI 解析を担っていた名残。
 * 後続 SBI で Step2 を作り直す際に改名する想定で、差分を抑えるため据え置く。
 */
import { Spinner } from '../Spinner';
import { useState } from 'react';
import type React from 'react';

import {
  CREATE_DICTIONARY_AUDIO_BUTTON_LABEL,
  MAX_TUNING_TEXT_LENGTH,
  RESET_BUTTON_LABEL,
  STEP2_TITLE,
} from './constants';
import { createDictionarySsml } from './dictionarySsml';
import type { ApplyDictionaryFn } from './dictionarySsml';
import { MAX_EDITED_SSML_LENGTH, prepareEditedSsml } from './ssmlDraft';
import { STEP2_AUDIO_TITLE, StepAudioControls } from './StepAudioControls';
import type { PreviewAudioFn } from './useSsmlAudio';

export interface Step1AnalyzePanelProps {
  /** 放送テキスト (親が保持する値 / 制御コンポーネント)。 */
  text: string;
  /** 放送テキストが編集されたときに呼ぶ。 */
  onTextChange: (text: string) => void;
  /**
   * 音声合成関数 (`POST /audio-preview`)。SBI-002。
   *
   * 指定すると放送テキストエリアの下に音声操作 (Audio プレイヤー / 変更内容でAudio再作成 /
   * ダウンロード) を出す。プレーンテキストをそのまま Polly へ渡す (辞書は適用しない)。
   * SBI-005: Step2 の SSML 編集欄の下にも同じ音声操作を出す (編集した SSML を Polly へ渡す)。
   * 省略時は音声操作を出さない (音声を扱わない描画のためのテスト用途)。
   */
  previewAudio?: PreviewAudioFn;
  /**
   * 辞書適用を実行する関数 (`POST /audio-preview` の `applied_ssml` を使う)。SBI-003。
   *
   * 指定すると Step1 に「辞書適用Audio作成」ボタンを出し、押すと放送テキストへ SSML 辞書を
   * 適用した SSML を作って Step2 へ表示する。省略時はボタンを出さない (辞書を扱わない描画のためのテスト用途)。
   */
  applyDictionary?: ApplyDictionaryFn;
  /**
   * Step2 最下部の差し込み口 (要素) を親へ渡す callback ref (UI/UX 改善 Phase2)。
   *
   * 「放送テキストを確認 → Audio サンプル作成」の流れにするため、Step3 の作成ボタンを
   * Step2 の最下部へ描画する (`Step3SamplePanel` の `actionsContainer` と対で使う)。
   * 本コンポーネントは差し込み口を用意するだけで、中身 (ボタン) の状態は知らない。
   */
  step2ActionsRef?: (element: HTMLDivElement | null) => void;
  /**
   * 「リセット」ボタンが押されたときに呼ぶ (検証内容の全クリア)。
   *
   * 指定すると Step1 のタイトル行右側に「リセット」ボタンを出す。
   * ⚠️ 本コンポーネントは **確認ダイアログも初期化も行わない**。
   * リセット対象は Step1〜Step3 の全体で、Step3 の状態は本コンポーネントの外にあるため、
   * 確認とクリアはページ側が一括で担う (Step1 だけ消えて Step3 が残る事故を防ぐ)。
   */
  onResetRequest?: () => void;
}

export function Step1AnalyzePanel({
  text,
  onTextChange,
  previewAudio,
  applyDictionary,
  step2ActionsRef,
  onResetRequest,
}: Step1AnalyzePanelProps): React.JSX.Element {
  // 空白のみの入力は「未入力」と同じ扱いにする (Step3 / サーバも 400 を返す)。
  const hasText = text.trim() !== '';

  /** Step2 に表示・編集する辞書適用済 SSML (未作成は `null` / 利用者の編集が反映される)。 */
  const [dictionarySsml, setDictionarySsml] = useState<string | null>(null);
  /** 辞書が 1 件以上効いたか (効かなかったときの注記に使う)。 */
  const [dictionaryApplied, setDictionaryApplied] = useState(false);
  /** 現在の Step2 が **どの放送テキストから作られたか** (編集後の取り違え防止)。 */
  const [sourceOfDictionarySsml, setSourceOfDictionarySsml] = useState<string | null>(null);
  const [isCreatingDictionary, setIsCreatingDictionary] = useState(false);
  const [dictionaryError, setDictionaryError] = useState<string | null>(null);

  // 放送テキストを編集したのに「辞書適用Audio作成」を押し直していない状態 (前後の空白差は無視)。
  const isDictionaryStale =
    dictionarySsml !== null &&
    sourceOfDictionarySsml !== null &&
    text.trim() !== sourceOfDictionarySsml.trim();

  const createDisabled = !hasText || isCreatingDictionary;
  const createTitle = ((): string | undefined => {
    if (!hasText) {
      return '放送テキストを入力すると辞書適用Audioを作成できます。';
    }
    if (isCreatingDictionary) {
      return '辞書適用Audioを作成中です。';
    }
    return undefined;
  })();

  async function handleCreateDictionary(): Promise<void> {
    if (applyDictionary === undefined || createDisabled) {
      return;
    }
    const requestedText = text;
    setIsCreatingDictionary(true);
    setDictionaryError(null);
    const result = await createDictionarySsml(requestedText, applyDictionary);
    setIsCreatingDictionary(false);
    if (!result.ok) {
      // 失敗時は前回の Step2 を残す (消すと直前までの確認内容が失われる)。
      setDictionaryError(result.error);
      return;
    }
    setDictionarySsml(result.ssml);
    setDictionaryApplied(result.dictionaryApplied);
    setSourceOfDictionarySsml(requestedText);
  }

  return (
    <>
      <section
        className="ssml-tuning-step"
        data-testid="ssml-tuning-step1"
        aria-labelledby="ssml-tuning-step1-title"
      >
        {/*
          タイトル行: [Step1 放送テキスト入力] ........ [リセット]
          ⚠️ リセットは Step1 だけでなく **Step1〜Step3 全体** を初期化する
          (確認ダイアログとクリア処理はページ側)。ボタンは h3 の外に置き、
          見出しのアクセシブルネームを変えない。
        */}
        <div className="ssml-tuning-step__title-row ssml-tuning-step__title-row--with-action">
          <h3 className="ssml-tuning-step__title" id="ssml-tuning-step1-title">
            Step1 放送テキスト入力
          </h3>
          {onResetRequest !== undefined && (
            /*
              赤系アウトライン + ゴミ箱アイコン (見た目は `.ssml-tuning-reset-button`)。
              アイコンは装飾なので aria-hidden (ボタンの名前は「リセット」のまま)。
            */
            <button
              type="button"
              className="ssml-tuning-button ssml-tuning-button--small ssml-tuning-reset-button"
              data-testid="ssml-tuning-reset-button"
              onClick={onResetRequest}
              title="Step1〜Step3 の検証内容をすべてクリアして初期状態に戻します"
            >
              <svg
                className="ssml-tuning-reset-button__icon"
                data-testid="ssml-tuning-reset-icon"
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M3 6h18" />
                <path d="M8 6V4h8v2" />
                <path d="M6 6l1 14h10l1-14" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
              </svg>
              {RESET_BUTTON_LABEL}
            </button>
          )}
        </div>

        {/*
          文字数カウンタ行 (右寄せ)。
          「放送テキスト」ラベルは廃止した (見出し「Step1 放送テキスト入力」と重複するため)。
          ⚠️ textarea のアクセシブルネームは見出し (`aria-labelledby`) が担う。
          SBI-010: 「必須」表示は廃止した (未入力時はボタンが無効になるため視覚的ノイズ)。
          支援技術には textarea の `aria-required` で必須であることを引き続き伝える。
        */}
        <div className="ssml-tuning-field">
          <div className="ssml-tuning-field__header">
            <span className="ssml-tuning-field__counter" data-testid="ssml-tuning-text-counter">
              {text.length} / {MAX_TUNING_TEXT_LENGTH}文字
            </span>
          </div>
          <textarea
            id="ssml-tuning-text"
            className="ssml-tuning-field__textarea"
            data-testid="ssml-tuning-text"
            aria-labelledby="ssml-tuning-step1-title"
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            rows={4}
            maxLength={MAX_TUNING_TEXT_LENGTH}
            placeholder="例) この電車は3分遅れで運転しています"
            aria-required="true"
          />
        </div>

        {/* SBI-002: 放送テキストエリア直下の音声操作 (試聴 / 再作成 / ダウンロード)。 */}
        {previewAudio !== undefined && (
          <StepAudioControls text={text} previewAudio={previewAudio} />
        )}

        {/*
          SBI-003: Step1 → Step2 へ進む操作。押したときだけ辞書を適用した SSML を作る。
          ⚠️ 失敗しても Step1 の入力は消さない (エラーは直下に出す)。
        */}
        {applyDictionary !== undefined && (
          <div className="ssml-tuning-step__footer" data-testid="ssml-tuning-step1-actions">
            <button
              type="button"
              className="ssml-tuning-button ssml-tuning-button--primary"
              data-testid="ssml-tuning-dictionary-button"
              onClick={() => void handleCreateDictionary()}
              disabled={createDisabled}
              title={createTitle}
            >
              {isCreatingDictionary ? (
                <>
                  <Spinner />
                  辞書適用中…
                </>
              ) : (
                CREATE_DICTIONARY_AUDIO_BUTTON_LABEL
              )}
            </button>
            {dictionaryError !== null && (
              <p
                className="ssml-tuning-error"
                role="alert"
                data-testid="ssml-tuning-dictionary-error"
              >
                {dictionaryError}
              </p>
            )}
          </div>
        )}
      </section>

      <section
        className="ssml-tuning-step"
        data-testid="ssml-tuning-step2"
        aria-labelledby="ssml-tuning-step2-title"
      >
        <div className="ssml-tuning-step__title-row">
          <h3 className="ssml-tuning-step__title" id="ssml-tuning-step2-title">
            {STEP2_TITLE}
          </h3>
        </div>

        {dictionarySsml !== null ? (
          <>
            {/*
              SBI-004: 辞書適用結果を確認・編集する欄。
              textarea は改行をそのまま保持し、長い SSML は欄内スクロールで読める。
              ⚠️ 構文チェックはここでしない (Polly の判断に委ねる / ssmlDraft.ts 参照)。
            */}
            <div className="ssml-tuning-field">
              <div className="ssml-tuning-field__header">
                <span
                  className="ssml-tuning-field__counter"
                  data-testid="ssml-tuning-step2-counter"
                >
                  {dictionarySsml.length} / {MAX_EDITED_SSML_LENGTH}文字
                </span>
              </div>
              <textarea
                id="ssml-tuning-step2-ssml"
                className="ssml-tuning-step2__ssml"
                data-testid="ssml-tuning-step2-ssml"
                aria-labelledby="ssml-tuning-step2-title"
                value={dictionarySsml}
                onChange={(e) => setDictionarySsml(e.target.value)}
                rows={10}
                maxLength={MAX_EDITED_SSML_LENGTH}
                spellCheck={false}
                wrap="soft"
              />
            </div>
            {/*
              SBI-005: SSML 表示エリア直下の音声操作 (試聴 / 変更内容でAudio再作成 / ダウンロード)。
              編集中の SSML をそのまま Polly へ渡す (辞書は再適用しない)。
              ダウンロード名は Step1 の放送テキスト由来 (`遅延案内_辞書適用.mp3`)。
              ⚠️ Step1 の放送テキストを編集しても、Step2 の音声は作り直さない (押したときだけ合成)。
            */}
            {previewAudio !== undefined && (
              <StepAudioControls
                text={dictionarySsml}
                previewAudio={previewAudio}
                testIdPrefix="ssml-tuning-step2"
                audioTitle={STEP2_AUDIO_TITLE}
                fileNameText={text}
                prepareSsml={prepareEditedSsml}
                emptyReason="SSML を入力すると音声を作成できます。"
              />
            )}
            {!dictionaryApplied && (
              <p className="ssml-tuning-step__note" data-testid="ssml-tuning-step2-no-match">
                辞書に該当する語がありませんでした (放送テキストをそのまま SSML にしています)。
              </p>
            )}
            {isDictionaryStale && (
              <p className="ssml-audio-controls__dirty" data-testid="ssml-tuning-step2-stale">
                放送テキストが変更されています。「{CREATE_DICTIONARY_AUDIO_BUTTON_LABEL}
                」を押すと反映されます (Step2 の編集内容は辞書適用の結果で置き換わります)。
              </p>
            )}
          </>
        ) : (
          <p className="ssml-tuning-step__placeholder" data-testid="ssml-tuning-step2-empty">
            Step1 で放送テキストを入力して「{CREATE_DICTIONARY_AUDIO_BUTTON_LABEL}
            」を押すと、辞書適用済みの SSML がここに表示されます。
          </p>
        )}

        {/*
          Step2 最下部の差し込み口 (Audio サンプル作成ボタンが入る)。
          ⚠️ 入力前も常に置く。入力前後で要素が付け外しされると
          差し込み先が変わり、ボタンの状態表示 (押せない理由) がちらつく。
        */}
        {step2ActionsRef !== undefined && (
          <div
            className="ssml-tuning-step__footer"
            data-testid="ssml-tuning-step2-actions"
            ref={step2ActionsRef}
          />
        )}
      </section>
    </>
  );
}
